/**
 * @fileoverview Schema service for form schema management
 * @module services/schemaService
 * 
 * Provides API client methods for CRUD operations on form schemas.
 * Handles communication with the backend /api/schemas endpoints.
 * 
 * @see {@link https://github.com/hk-snow/mrfusion_ui_test/blob/main/specs/004-unified-form-schema/contracts/schema-api.openapi.yaml}
 */

import { apiClient } from './apiClient';
import {
  getPrototypeSchema,
  isPrototypeSchemaId,
  prototypeSchemaList,
  prototypeSchemaVersions,
} from '../sampleSchemas';

// Shared schema API types live in ./schemaTypes so data modules (../sampleSchemas)
// can use them without importing this service (breaks the circular dependency, #180).
// Re-exported below so existing `import { FormSchema } from '../services/schemaService'`
// call sites keep working.
import type { SchemaMetadata, FormSchema, SchemaVersion } from './schemaTypes';

export type { SchemaMetadata, FormSchema, SchemaVersion };

/**
 * List schemas request parameters
 */
export interface ListSchemasParams {
  legalPracticeId?: string;
  status?: 'draft' | 'published' | 'deprecated';
  limit?: number;
  offset?: number;
}

/**
 * Schema usage statistics
 */
export interface SchemaUsage {
  schemaId: string;
  usageCount: number;
  submissionCount: number;
  workflows: Array<{
    workflowName: string;
    stepName: string;
    status: 'active' | 'deprecated';
  }>;
  submissionCounts: {
    last24h: number;
    last7d: number;
    last30d: number;
  };
}

type SchemaMetadataLike = Partial<Omit<SchemaMetadata, 'metadata'>> & {
  metadata?: Partial<NonNullable<SchemaMetadata['metadata']>>;
};

const normalizeSchemaMetadata = (input: SchemaMetadataLike): SchemaMetadata => {
  const nowIso = new Date().toISOString();
  const createdAt = input.createdAt ?? input.metadata?.createdAt ?? nowIso;
  const updatedAt = input.updatedAt ?? input.metadata?.lastModifiedAt ?? createdAt;
  const status = input.status ?? input.metadata?.status ?? 'draft';
  const legalPracticeId = input.legalPracticeId ?? input.metadata?.legalPracticeId ?? 'prototype';
  const usageCount = input.usageCount ?? input.metadata?.usageCount ?? 0;
  const createdBy = input.createdBy ?? input.metadata?.createdBy ?? 'unknown';
  const updatedBy = input.updatedBy ?? input.metadata?.lastModifiedBy ?? input.metadata?.createdBy;

  return {
    id: input.id ?? 'unknown-schema',
    version: input.version ?? 1,
    title: input.title ?? 'Untitled schema',
    description: input.description ?? '',
    legalPracticeId,
    status,
    usageCount,
    createdAt,
    updatedAt,
    createdBy,
    updatedBy,
    s3Key: input.s3Key,
    s3VersionId: input.s3VersionId,
    metadata: {
      createdAt,
      lastModifiedAt: input.metadata?.lastModifiedAt ?? updatedAt,
      createdBy,
      lastModifiedBy: updatedBy,
      legalPracticeId,
      status,
      usageCount,
    },
  };
};

const normalizeFormSchema = (schema: FormSchema): FormSchema => {
  const normalized = normalizeSchemaMetadata(schema);
  return {
    ...schema,
    ...normalized,
    metadata: normalized.metadata,
  };
};

const cloneMetadata = (metadata: SchemaMetadata): SchemaMetadata => normalizeSchemaMetadata({ ...metadata });

const cloneSchema = (schema: FormSchema): FormSchema => normalizeFormSchema(JSON.parse(JSON.stringify(schema)) as FormSchema);

const cloneVersion = (version: SchemaVersion): SchemaVersion => ({ ...version });

const filterPrototypeMetadata = (params?: ListSchemasParams): SchemaMetadata[] => {
  let results = prototypeSchemaList;

  if (params?.legalPracticeId) {
    results = results.filter((item) => item.legalPracticeId === params.legalPracticeId);
  }

  if (params?.status) {
    results = results.filter((item) => item.status === params.status);
  }

  if (typeof params?.offset === 'number') {
    results = results.slice(params.offset);
  }

  if (typeof params?.limit === 'number') {
    results = results.slice(0, params.limit);
  }

  return results.map(cloneMetadata);
};

/**
 * Schema service for managing form schemas
 */
export const schemaService = {
  /**
   * List all schemas with optional filters
   * 
   * @param params - Filter parameters (legalPracticeId, status, pagination)
   * @returns Array of schema metadata objects
   * 
   * @example
   * const schemas = await schemaService.listSchemas({ 
   *   legalPracticeId: 'LP001', 
   *   status: 'published',
   *   limit: 20 
   * });
   */
  async listSchemas(params?: ListSchemasParams): Promise<SchemaMetadata[]> {
    const queryParams = new URLSearchParams();
    if (params?.legalPracticeId) queryParams.append('legalPracticeId', params.legalPracticeId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const url = `/api/schemas${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    try {
      const response = await apiClient.get(url);
        const payload = response.data;
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.schemas)
          ? payload.schemas
          : [];

        if (!Array.isArray(payload) && !Array.isArray(payload?.schemas)) {
          console.warn('schemaService.listSchemas received unexpected response shape', payload);
        }

        return items.map((item: SchemaMetadataLike) => normalizeSchemaMetadata(item));
    } catch (error) {
      console.warn('schemaService.listSchemas falling back to prototype data', error);
      return filterPrototypeMetadata(params);
    }
  },

  /**
   * Get a specific schema by ID (latest version)
   * 
   * @param id - Schema UUID
   * @returns Full schema with properties
   * 
   * @example
   * const schema = await schemaService.getSchema('550e8400-e29b-41d4-a716-446655440000');
   */
  async getSchema(id: string): Promise<FormSchema> {
    if (isPrototypeSchemaId(id)) {
      const localSchema = getPrototypeSchema(id);
      if (localSchema) {
        return cloneSchema(localSchema);
      }
    }

    try {
      const response = await apiClient.get(`/api/schemas/${id}`);
        return normalizeFormSchema(response.data as FormSchema);
    } catch (error) {
      const fallback = getPrototypeSchema(id);
      if (fallback) {
        console.warn(`schemaService.getSchema fallback for ${id}`, error);
          return cloneSchema(fallback);
      }
      throw error;
    }
  },

  /**
   * Create a new schema
   * 
   * @param schema - Schema object with title, type, properties
   * @returns Created schema metadata with ID and version
   * 
   * @example
   * const newSchema = await schemaService.createSchema({
   *   title: 'Client Intake Form',
   *   description: 'Basic client information',
   *   legalPracticeId: 'LP001',
   *   type: 'object',
   *   properties: {
   *     clientName: { type: 'string', title: 'Client Name' }
   *   }
   * });
   */
  async createSchema(schema: Partial<FormSchema>): Promise<SchemaMetadata> {
    const response = await apiClient.post('/api/schemas', schema);
    return normalizeSchemaMetadata(response.data as SchemaMetadataLike);
  },

  /**
   * Update an existing schema (creates new version)
   * 
   * @param id - Schema UUID
   * @param updates - Partial schema updates
   * @param currentVersion - Current version number for optimistic locking
   * @returns Updated schema metadata with new version
   * 
   * @example
   * const updated = await schemaService.updateSchema(
   *   '550e8400-e29b-41d4-a716-446655440000',
   *   { title: 'Updated Title' },
   *   1
   * );
   */
  async updateSchema(id: string, updates: Partial<FormSchema>, currentVersion: number | string): Promise<SchemaMetadata> {
    const response = await apiClient.put(`/api/schemas/${id}`, {
      ...updates,
      currentVersion: currentVersion.toString(),
    });
    return normalizeSchemaMetadata(response.data as SchemaMetadataLike);
  },

  /**
   * Deprecate a schema (soft delete)
   * 
   * @param id - Schema UUID
   * @returns Updated schema metadata with deprecated status
   * 
   * @example
   * await schemaService.deprecateSchema('550e8400-e29b-41d4-a716-446655440000');
   */
  async deprecateSchema(id: string): Promise<SchemaMetadata> {
    const response = await apiClient.delete(`/api/schemas/${id}`);
    return normalizeSchemaMetadata(response.data as SchemaMetadataLike);
  },

  /**
   * List all versions of a schema
   * 
   * @param id - Schema UUID
   * @returns Array of version metadata (limit 10, newest first)
   * 
   * @example
   * const versions = await schemaService.listVersions('550e8400-e29b-41d4-a716-446655440000');
   */
  async listVersions(id: string): Promise<SchemaVersion[]> {
    if (isPrototypeSchemaId(id)) {
      return (prototypeSchemaVersions[id] ?? []).map(cloneVersion);
    }

    try {
      const response = await apiClient.get(`/api/schemas/${id}/versions`);
      return response.data;
    } catch (error) {
      if (prototypeSchemaVersions[id]) {
        console.warn(`schemaService.listVersions fallback for ${id}`, error);
        return prototypeSchemaVersions[id].map(cloneVersion);
      }
      throw error;
    }
  },

  /**
   * Get a specific version of a schema
   * 
   * @param id - Schema UUID
   * @param versionId - S3 version ID
   * @returns Full schema for that version
   * 
   * @example
   * const v2 = await schemaService.getVersion(
   *   '550e8400-e29b-41d4-a716-446655440000',
   *   'aHR0cHM6Ly9leGFtcGxlLmNvbS92ZXJzaW9u'
   * );
   */
  async getVersion(id: string, versionId: string): Promise<FormSchema> {
    if (isPrototypeSchemaId(id)) {
      const schema = getPrototypeSchema(id);
      if (schema && (prototypeSchemaVersions[id] ?? []).some((version) => version.versionId === versionId)) {
        return cloneSchema(schema);
      }
    }

    try {
      const response = await apiClient.get(`/api/schemas/${id}/versions/${versionId}`);
        return normalizeFormSchema(response.data as FormSchema);
    } catch (error) {
      const schema = getPrototypeSchema(id);
      if (schema && (prototypeSchemaVersions[id] ?? []).some((version) => version.versionId === versionId)) {
        console.warn(`schemaService.getVersion fallback for ${id} (${versionId})`, error);
          return cloneSchema(schema);
      }
      throw error;
    }
  },

  /**
   * Duplicate a schema with a new title
   * 
   * @param id - Source schema UUID
   * @param newTitle - Title for the duplicated schema
   * @returns New schema metadata with new ID
   * 
   * @example
   * const duplicate = await schemaService.duplicateSchema(
   *   '550e8400-e29b-41d4-a716-446655440000',
   *   'Copy of Client Intake Form'
   * );
   */
  async duplicateSchema(id: string, newTitle: string): Promise<SchemaMetadata> {
    const response = await apiClient.post(`/api/schemas/${id}/duplicate`, {
      newTitle,
    });
    return normalizeSchemaMetadata(response.data as SchemaMetadataLike);
  },

  /**
   * Get usage statistics for a schema
   * 
   * @param id - Schema UUID
   * @returns Usage statistics including workflow dependencies
   * 
   * @example
   * const usage = await schemaService.getUsage('550e8400-e29b-41d4-a716-446655440000');
   * console.log(`Used in ${usage.workflows.length} workflows`);
   */
  async getUsage(id: string): Promise<SchemaUsage> {
    const response = await apiClient.get(`/api/schemas/${id}/usage`);
    return response.data;
  },

  /**
   * Get UI schema for a workflow from S3
   * 
   * This fetches the dynamic UI schema that defines how to render
   * review/edit forms for a specific workflow. UI schemas are stored
   * alongside recipe files in S3.
   * 
   * @param practice - Practice area (e.g., 'corporate', 'real-estate')
   * @param workflow - Workflow name (e.g., 'airplane-lease-step-one')
   * @returns UI schema object with form definitions and metadata, or null if not found
   * 
   * @example
   * const uiSchema = await schemaService.getWorkflowUiSchema('corporate', 'airplane-lease-step-one');
   * if (uiSchema) {
   *   console.log('Form sections:', uiSchema.uiSchema.form_schema.sections);
   * }
   */
  async getWorkflowUiSchema(practice: string, workflow: string): Promise<WorkflowUiSchemaResponse | null> {
    try {
      const response = await apiClient.get(`/api/recipes/${practice}/${workflow}/ui-schema`);
      return response.data as WorkflowUiSchemaResponse;
    } catch (error: any) {
      // Return null for 404 (schema not found), throw for other errors
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

/**
 * UI Schema response from the API
 */
export interface WorkflowUiSchemaResponse {
  practice: string;
  workflow: string;
  uiSchema: WorkflowUiSchema;
}

/**
 * Unified workflow configuration - display multi-step workflows as single job
 */
export interface UnifiedWorkflowConfig {
  /** Enable unified view (single job card for all steps) */
  enabled: boolean;
  /** Display name for the unified workflow (e.g., "Ocean Bank Term Sheet") */
  display_name?: string;
  /** Unified ID for URL/tracking (e.g., "ocean-bank") */
  unified_id?: string;
  /** List of all workflow steps in order */
  steps: Array<{
    workflow: string;
    practice: string;
    label: string;  // Human-readable step label
  }>;
}

/**
 * Step control configuration - manual controls for workflow steps
 */
export interface StepControlsConfig {
  /** Allow downloading intermediate output (e.g., JSON from step one) */
  allow_intermediate_download?: boolean;
  /** Types to allow download for intermediate steps */
  downloadable_types?: string[];
  /** Allow editing intermediate output before continuing */
  allow_intermediate_edit?: boolean;
  /** Allow manually triggering the next step (vs auto-continue) */
  allow_manual_trigger?: boolean;
  /** Text for download button */
  download_button_text?: string;
  /** Text for edit button */
  edit_button_text?: string;
  /** Text for manual trigger button */
  trigger_button_text?: string;
}

/**
 * Step display configuration - how to show progress within unified job
 */
export interface StepDisplayConfig {
  /** Show step progress indicator */
  show_step_progress?: boolean;
  /** Style of progress indicator */
  progress_style?: 'stepper' | 'pills' | 'minimal';
  /** Custom labels for each step (overrides step.label) */
  step_labels?: string[];
}

/**
 * Input display configuration - controls how original input documents are shown
 */
export interface InputDisplayConfig {
  /** show the original input documents panel (default: true) */
  show_original_documents?: boolean;
  /** where to show the document viewer */
  viewer_position?: 'side' | 'tab' | 'hidden';
  /** allow downloading the original input files */
  allow_input_download?: boolean;
  /** custom heading for the documents panel (default: "Original Document") */
  panel_title?: string;
}

/**
 * File upload configuration - controls upload behavior for this workflow
 */
export interface FileUploadConfig {
  /** max file size in MB */
  max_size_mb?: number;
  /** accepted MIME types or extensions (e.g. [".pdf", ".docx"]) */
  accepted_types?: string[];
  /** allow multiple files per submission */
  multiple_files?: boolean;
  /** custom message shown in the upload area */
  upload_message?: string;
}

/**
 * Workflow UI Schema definition - matches the actual S3 schema format
 */
export interface WorkflowUiSchema {
  metadata: {
    practice: string;
    workflow: string;
    display_name: string;
    description?: string;
    version?: string;
  };
  /** Whether this is an internal step not shown to users */
  internal?: boolean;
  /** Whether to show this workflow in the sidebar navigation */
  show_in_sidebar?: boolean;
  workflow_type?: 'multi-step-first' | 'multi-step-middle' | 'multi-step-final' | 'single-step';
  
  /** Unified workflow config - display multi-step as single job */
  unified_workflow?: UnifiedWorkflowConfig;
  
  /** Step controls - manual download, edit, trigger options */
  step_controls?: StepControlsConfig;
  
  /** Step display - how to show progress within unified job */
  step_display?: StepDisplayConfig;
  
  multi_step_config?: {
    next_workflow?: {
      practice: string;
      workflow: string;
    };
    review_required?: boolean;
    auto_continue?: boolean;
  };
  output_handling?: {
    primary_output_type?: string;
    show_download_for_types?: string[];
    hide_output_types?: string[];
    /** Allow downloading JSON output even if hidden from main display */
    allow_json_download?: boolean;
    /** Template for download filenames: {job_number}, {workflow}, {practice}, {extension}, {original_name}, {date} */
    download_filename_template?: string;
  };
  completion_actions?: {
    show_review_button?: boolean;
    review_button_text?: string;
    continue_button_text?: string;
    completion_message?: string;
  };
  /** Help text shown when user clicks help button */
  help_text?: {
    overview?: string;
    sample_instructions?: string;
    tips?: string[];
  };
  /** Custom execution step labels for progress display */
  execution_steps?: string[];
  /** Controls how original input documents are displayed */
  input_display?: InputDisplayConfig;
  /** Controls file upload behavior for this workflow */
  file_upload?: FileUploadConfig;
  review_form?: ReviewFormConfig;
  // Legacy format support
  continuation?: {
    enabled: boolean;
    next_workflow?: string;
    review_screen?: {
      title?: string;
      description?: string;
      submit_button_text?: string;
    };
  };
  form_schema?: {
    type: 'object';
    properties: Record<string, WorkflowFormProperty>;
    required?: string[];
    sections: WorkflowFormSection[];
  };
  layout_hints?: {
    form_layout?: 'single-column' | 'two-column' | 'accordion';
    card_style?: 'elevated' | 'flat';
    section_collapsible?: boolean;
  };
}

/**
 * Field override for hybrid mode - customize auto-derived fields
 */
export interface FieldOverride {
  type?: ReviewFormField['type'];
  label?: string;
  description?: string;
  grid_width?: number;
  options?: string[]; // For select/dropdown type
  multiline_rows?: number;
  /** Assign this field to a specific section */
  section?: string;
  /** Order within section (lower numbers first) */
  order?: number;
  /** Hide this field from the form */
  hidden?: boolean;
  /** Conditionally show this field based on another field's value */
  show_if?: { field: string; value: string | string[] };
}

/**
 * Section configuration for hybrid mode
 */
export interface SectionOverride {
  id: string;
  title: string;
  icon?: string;
  default_expanded?: boolean;
  /** Fields to include in this section (by key) */
  include_fields?: string[];
}

/**
 * Section override configuration for hybrid mode
 */
export interface SectionOverrideConfig {
  /** Custom section definitions */
  custom_sections?: SectionOverride[];
  /** Override which section a field belongs to */
  field_section_map?: Record<string, string>;
  /** Order sections should appear */
  section_order?: string[];
}

/**
 * Review form configuration
 */
export interface ReviewFormConfig {
  enabled: boolean;
  title: string;
  description?: string;
  show_source_document?: boolean;
  submit_button_text?: string;
  cancel_button_text?: string;
  save_button_text?: string;
  sections?: ReviewFormSection[];
  /** Hybrid mode: derive fields from data with optional overrides */
  derive_from_data?: boolean;
  /** Field-level presentation overrides for hybrid mode */
  field_overrides?: Record<string, FieldOverride>;
  /** Section-level overrides for hybrid mode */
  section_overrides?: SectionOverrideConfig;
  /** Fields to hide from derived form */
  hidden_fields?: string[];
}

/**
 * Section in the review form
 */
export interface ReviewFormSection {
  id: string;
  title: string;
  icon?: string;
  collapsible?: boolean;
  default_expanded?: boolean;
  subsections?: ReviewFormSubsection[];
  fields?: ReviewFormField[];
}

/**
 * Subsection within a section
 */
export interface ReviewFormSubsection {
  id: string;
  title: string;
  fields: ReviewFormField[];
}

/**
 * Field definition in the review form
 */
export interface ReviewFormField {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'dropdown' | 'multiline' | 'array' | 'text' | 'textarea' | 'date' | 'currency' | 'percentage';
  label: string;
  description?: string;
  grid_width?: number; // 1-12 for MUI Grid
  options?: string[]; // For select/dropdown type
  multiline_rows?: number;
  /** Conditionally show this field based on another field's value */
  show_if?: { field: string; value: string | string[] };
}

/**
 * Form property definition in UI schema
 */
export interface WorkflowFormProperty {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  title?: string;
  description?: string;
  default?: any;
  enum?: string[];
  enumNames?: string[];
  format?: string;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  multiline?: boolean;
  rows?: number;
  items?: WorkflowFormProperty;
  properties?: Record<string, WorkflowFormProperty>;
}

/**
 * Form section definition for grouping fields
 */
export interface WorkflowFormSection {
  id: string;
  title: string;
  description?: string;
  fields: string[];
  collapsible?: boolean;
  defaultExpanded?: boolean;
}
